import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { CopyIcon } from 'lucide-react';

import { notificationService } from '@/lib/services/notification.service';

export interface CopyButtonProps {
  /** The text placed on the clipboard. */
  value: string;
  /** Names the control, and labels the tooltip. Defaults to a generic "Copy". */
  label?: string;
  /** Announced on success. Defaults to a generic "Copied". */
  successMessage?: string;
  className?: string;
  /**
   * Passed straight to the button, so a host that mounts one of these per row can
   * keep them out of the tab sequence and expose only the one that is current.
   * Omit it and the button is an ordinary tab stop.
   */
  tabIndex?: number;
  /**
   * Hides the control from assistive technology. Pair it with `tabIndex={-1}`:
   * a host that reveals one of these on hover still MOUNTS them all, and being
   * visually transparent does not remove an element from the accessibility tree,
   * so a screen reader would otherwise meet one "Copy" button per rendered row.
   * Never set it on a control that is reachable, which is why it is separate
   * from `tabIndex` rather than inferred from it.
   */
  'aria-hidden'?: boolean;
}

/**
 * Copies a string, and says so.
 *
 * Exists because `navigator.clipboard.writeText` was hand-rolled at thirteen call
 * sites, each re-deriving the same try/catch and its own toast — and the clipboard
 * rejects for reasons a caller cannot predict (a document without focus, a denied
 * permission, an insecure origin), so a missing catch fails silently.
 *
 * `mousedown` is prevented so a click cannot pull focus out of whatever owns it.
 * That matters inside a grid, where focus lives on the container and drives arrow
 * navigation; elsewhere it is harmless.
 */
export function CopyButton({
  value,
  label,
  successMessage,
  className,
  tabIndex,
  'aria-hidden': ariaHidden,
}: CopyButtonProps) {
  const { t } = useTranslation('common');
  const name = label ?? t('components.copyButton.copy');

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      notificationService.showSuccess(successMessage ?? t('components.copyButton.copied'));
    } catch {
      notificationService.showError(t('components.copyButton.failed'));
    }
  }, [value, successMessage, t]);

  return (
    /*
      Its own provider. Radix throws rather than degrading when one is missing, and
      this is a `shared` primitive with no say in where it is mounted — several of
      its hosts render outside any provider, and every one of them would throw.
      Nesting under an existing provider is harmless.
    */
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon-mini"
            className={className}
            aria-label={name}
            tabIndex={tabIndex}
            aria-hidden={ariaHidden}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => void copy()}
          >
            <CopyIcon className="size-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">{name}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
